import React, { useEffect, useState } from "react";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import FormInput from "../../../components/UI/FormInput";
import FormSelect from "../../../components/UI/FormSelect";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import {
  updatePatient,
  updatePatientChart,
} from "../../../services/patientService";
import {
  UserIcon,
  ShieldIcon,
  MapPinIcon,
  PhoneIcon,
  AlertCircleIcon,
  HelpingIcon,
  DoctorIcon,
  PillIcon,
  BuildingIcon,
  EditIcon,
  PrintIcon,
} from "../../../Icons";
import { displayDateUS } from "../../../utils/dateUtils";
import { formatPhone } from "../../Application/application.validation";
import CollapsibleSection from "./CollapsibleSection";
import PatientNotesSection from "./PatientNotesSection";
import { demographicsPrintUrl } from "../demographicsPrint";
import {
  REFERRAL_SOURCE_OPTIONS,
  RELATIONSHIP_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RACE_OPTIONS,
  RELIGION_OPTIONS,
  PHYSICIAN_TYPE_OPTIONS,
  PECOS_STATUS_OPTIONS,
  pecosStatusFromPmd,
  PHARMACY_TYPE_OPTIONS,
  HOSPITAL_TYPE_OPTIONS,
  buildEmergencyContacts,
  buildDiagnosisAllergies,
  buildAllergies,
  buildReferrals,
  buildInsurances,
  buildPhysicians,
  emptyEmergencyContact,
  emptyDiagnosisAllergy,
  emptyAllergy,
  emptyReferral,
  emptyInsurance,
  emptyPhysician,
  pickSectionPayload,
  validateDemographicsSection,
  parsePhysicianApiFieldErrors,
  ALLERGY_OTHER_VALUE,
  COMMON_ALLERGY_OPTIONS,
  CLINICAL_GROUP_OPTIONS,
} from "../patientForm.shared";
import {
  buildNutrition,
  NUTRITION_SECTION_TITLE,
} from "../patientNutrition.shared";
import {
  DiagnosisNutritionSubsection,
  DiagnosisNutritionSubsectionStack,
} from "./DiagnosisNutritionSubsection";
import { DiagnosisNutritionViewContent } from "./DiagnosisNutritionSectionContent";
import {
  NutritionDietEditFields,
  NutritionRestrictionEditFields,
} from "./NutritionSectionFields";
import useDemographicsLibraryOptions from "../useDemographicsLibraryOptions";
import {
  PrimaryFacilityEditFields,
  PrimaryFacilityViewFields,
} from "./PrimaryFacilitySection";
import InsuranceNameField from "./InsuranceNameField";
import {
  RowTextField,
  RowSelectField,
  RowDateField,
  RowBillableField,
  DiagnosisSearchField,
  PhysicianSearchField,
  AllergyRowFields,
  AddRowButton,
  RemoveRowButton,
} from "./MultiRowFields";
import ZipCodeInput from "./ZipCodeInput";
import { STATUS_LABELS, PATIENT_STATUS_SELECT_OPTIONS, SOC_DATE_LABEL, EOC_DATE_LABEL, statusRequiresEocDate } from "./StatusBadge";
import { patientSectionTones, PATIENT_THEME } from "../patientTheme";

const dateLabelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";
const dateInputClass = `block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none ${PATIENT_THEME.focus}`;

const Field = ({ label, value, className = "" }) => (
  <div className={`min-w-0 py-0.5 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-slate-900 font-medium break-words leading-snug">
      {value || "—"}
    </div>
  </div>
);

const FieldGrid = ({ children }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-5 gap-y-3 p-4">
    {children}
  </div>
);

const EditGrid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-3 p-4">
    {children}
  </div>
);

const buildRelationshipOptions = (current) => {
  if (current && !RELATIONSHIP_OPTIONS.some((o) => o.value === current)) {
    return [{ value: current, label: current }, ...RELATIONSHIP_OPTIONS];
  }
  return RELATIONSHIP_OPTIONS;
};

const SECTION_TONES = patientSectionTones([
  "personal",
  "insurance",
  "contact",
  "diagnosis",
  "referral",
  "physician",
  "pharmacy",
  "hospital",
  "emergency",
]);

const SectionCard = ({
  title,
  icon,
  tone = "personal",
  canEdit,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
  children,
}) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.personal;
  const [open, setOpen] = useState(true);

  // Keep section expanded while editing so fields stay visible.
  useEffect(() => {
    if (editing) setOpen(true);
  }, [editing]);

  const actions = canEdit ? (
    !editing ? (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          onEdit?.();
        }}
        className="inline-flex items-center justify-center h-7 w-7 text-indigo-700 bg-white border border-slate-200 rounded hover:bg-slate-50"
        aria-label="Edit"
        title="Edit"
      >
        <EditIcon size={13} />
      </button>
    ) : (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-7 px-2.5 text-xs font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className={`inline-flex items-center justify-center gap-1 h-7 px-2.5 text-xs font-medium border bg-white rounded disabled:opacity-50 ${t.edit}`}
        >
          {saving ? (
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : null}
          Save
        </button>
      </div>
    )
  ) : null;

  return (
    <CollapsibleSection
      title={title}
      icon={icon}
      tone={t}
      open={open}
      onOpenChange={setOpen}
      actions={actions}
    >
      {children}
    </CollapsibleSection>
  );
};

/** Generic multi-row section body: renders rows + Add button, tone-aware. */
const MultiRowSection = ({
  rows,
  renderRow,
  onAdd,
  onRemove,
  addLabel,
  tone,
  minRows = 0,
  className = "p-4 space-y-3",
}) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.personal;
  return (
    <div className={className}>
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-start border-b border-slate-100 last:border-0 pb-3 last:pb-0"
        >
          {renderRow(row, idx)}
          {rows.length > minRows && (
            <div className="lg:col-span-1 flex sm:justify-end self-end">
              <RemoveRowButton onClick={() => onRemove(idx)} label={`Remove row ${idx + 1}`} />
            </div>
          )}
        </div>
      ))}
      <AddRowButton label={addLabel} onClick={onAdd} tone={t.addTone} />
    </div>
  );
};

/** Read-only multi-row viewer, shown when a section isn't being edited. */
const ReadOnlyRows = ({ rows, emptyText, columns }) => {
  if (!rows.length) {
    return (
      <p className="px-4 py-6 text-center text-sm text-slate-500">{emptyText}</p>
    );
  }
  return (
    <div className="divide-y divide-slate-100">
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-2 px-4 py-3"
        >
          {columns.map((col) => (
            <Field
              key={col.key}
              label={
                typeof col.label === "function"
                  ? col.label(row, idx)
                  : col.label
              }
              value={col.format ? col.format(row[col.key]) : row[col.key]}
              className={col.className}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const buildDefaults = (patient) => ({
  firstName: patient.firstName || "",
  middleName: patient.middleName || "",
  lastName: patient.lastName || "",
  mrn: patient.mrn || "",
  dateOfBirth: displayDateUS(patient.dateOfBirth) || "",
  gender: patient.gender || "",
  status: patient.status || "Admit",
  admissionDate: displayDateUS(patient.admissionDate) || "",
  eocDate: displayDateUS(patient.eocDate) || "",
  language: patient.language || "",
  maritalStatus: patient.maritalStatus || "",
  race: patient.race || "",
  religion: patient.religion || "",
  email: patient.email || "",
  phone: patient.phone || "",
  alternatePhone: patient.alternatePhone || "",
  address: patient.address || "",
  addressLine2: patient.addressLine2 || "",
  city: patient.city || "",
  state: patient.state || "",
  zip: patient.zip || "",
  pharmacyName: patient.pharmacyName || "",
  pharmacyAddress: patient.pharmacyAddress || "",
  pharmacyCity: patient.pharmacyCity || "",
  pharmacyState: patient.pharmacyState || "",
  pharmacyZip: patient.pharmacyZip || "",
  pharmacyPhone: patient.pharmacyPhone || "",
  pharmacyFax: patient.pharmacyFax || "",
  pharmacyType: patient.pharmacyType || "",
  hospitalName: patient.hospitalName || "",
  hospitalAddress: patient.hospitalAddress || "",
  hospitalCity: patient.hospitalCity || "",
  hospitalState: patient.hospitalState || "",
  hospitalZip: patient.hospitalZip || "",
  hospitalPhone: patient.hospitalPhone || "",
  hospitalFax: patient.hospitalFax || "",
  hospitalType: patient.hospitalType || "",
});

const PatientDemographicsTab = ({
  patient,
  chartId,
  canEdit,
  canEditNotes,
  onSaved,
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const [emergencyContacts, setEmergencyContacts] = useState(() =>
    buildEmergencyContacts(patient),
  );
  const [diagnosisAllergies, setDiagnosisAllergies] = useState(() =>
    buildDiagnosisAllergies(patient),
  );
  const [allergies, setAllergies] = useState(() => buildAllergies(patient));
  const [nutrition, setNutrition] = useState(() => buildNutrition(patient));
  const [referrals, setReferrals] = useState(() => buildReferrals(patient));
  const [insurances, setInsurances] = useState(() => buildInsurances(patient));
  const [physicians, setPhysicians] = useState(() => buildPhysicians(patient));
  const [physicianFieldErrors, setPhysicianFieldErrors] = useState({});

  const form = useForm({ defaultValues: buildDefaults(patient) });
  const {
    register,
    reset,
    getValues,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = form;

  const watchedStatus = useWatch({ control, name: "status" });
  const showEocDate = statusRequiresEocDate(watchedStatus);

  useEffect(() => {
    if (!showEocDate) {
      setValue("eocDate", "");
      clearErrors("eocDate");
    }
  }, [showEocDate, setValue, clearErrors]);

  const {
    referralSourceOptions: libraryReferralOptions,
    findReferral,
  } = useDemographicsLibraryOptions();

  const ensureOption = (options, value) => {
    if (!value) return options;
    if (options.some((o) => o.value === value)) return options;
    return [...options, { value, label: value }];
  };

  useEffect(() => {
    if (!editingSection) {
      reset(buildDefaults(patient));
      setEmergencyContacts(buildEmergencyContacts(patient));
      setDiagnosisAllergies(buildDiagnosisAllergies(patient));
      setAllergies(buildAllergies(patient));
      setNutrition(buildNutrition(patient));
      setReferrals(buildReferrals(patient));
      setInsurances(buildInsurances(patient));
      setPhysicians(buildPhysicians(patient));
    }
  }, [patient, editingSection, reset]);

  const startEdit = (section) => {
    clearErrors();
    reset(buildDefaults(patient));
    if (section === "emergency") {
      const contacts = buildEmergencyContacts(patient);
      setEmergencyContacts(contacts.length ? contacts : [emptyEmergencyContact()]);
    }
    if (section === "diagnosis") {
      const rows = buildDiagnosisAllergies(patient);
      setDiagnosisAllergies(rows.length ? rows : [emptyDiagnosisAllergy()]);
      const allergyRows = buildAllergies(patient);
      setAllergies(allergyRows.length ? allergyRows : [emptyAllergy()]);
      setNutrition(buildNutrition(patient));
    }
    if (section === "referral") {
      const rows = buildReferrals(patient);
      setReferrals(rows.length ? rows : [emptyReferral()]);
    }
    if (section === "insuranceInfo") {
      const rows = buildInsurances(patient);
      setInsurances(rows.length ? rows : [emptyInsurance()]);
    }
    if (section === "physicianInfo") {
      const rows = buildPhysicians(patient);
      setPhysicians(rows.length ? rows : [emptyPhysician()]);
      setPhysicianFieldErrors({});
    }
    setEditingSection(section);
  };

  const cancelEdit = () => {
    clearErrors();
    reset(buildDefaults(patient));
    setEmergencyContacts(buildEmergencyContacts(patient));
    setDiagnosisAllergies(buildDiagnosisAllergies(patient));
    setAllergies(buildAllergies(patient));
    setNutrition(buildNutrition(patient));
    setReferrals(buildReferrals(patient));
    setInsurances(buildInsurances(patient));
    setPhysicians(buildPhysicians(patient));
    setPhysicianFieldErrors({});
    setEditingSection(null);
  };

  const arraySectionValues = {
    emergency: () => ({ emergencyContacts }),
    diagnosis: () => ({ diagnosisAllergies, allergies, nutrition }),
    referral: () => ({ referrals }),
    insuranceInfo: () => ({ insurances }),
    physicianInfo: () => ({ physicians }),
  };

  const saveSection = async (section) => {
    const values = arraySectionValues[section]
      ? arraySectionValues[section]()
      : getValues();
    const sectionErrors = validateDemographicsSection(section, values);
    clearErrors();
    if (section === "physicianInfo") {
      setPhysicianFieldErrors(sectionErrors);
    }
    const keys = Object.keys(sectionErrors);
    if (keys.length) {
      if (!arraySectionValues[section]) {
        keys.forEach((key) => {
          setError(key, { type: "manual", message: sectionErrors[key] });
        });
      }
      toast.error(sectionErrors[keys[0]]);
      return;
    }

    setSaving(true);
    try {
      const payload = pickSectionPayload(values, section);
      await updatePatient(patient.id, payload);
      // Stay fields (status / admission) also live on the chart for chart-wise UI.
      if (section === "personal" && chartId) {
        const chartPayload = {};
        if (payload.status != null) chartPayload.status = payload.status;
        if (payload.admissionDate != null) {
          chartPayload.startDate = payload.admissionDate;
        }
        if (statusRequiresEocDate(payload.status) && payload.eocDate != null) {
          chartPayload.dischargeDate = payload.eocDate;
        }
        if (Object.keys(chartPayload).length) {
          await updatePatientChart(patient.id, chartId, chartPayload);
        }
      }
      toast.success("Saved");
      setPhysicianFieldErrors({});
      setEditingSection(null);
      onSaved?.();
    } catch (err) {
      const apiFieldErrors = parsePhysicianApiFieldErrors(err.message);
      if (section === "physicianInfo" && Object.keys(apiFieldErrors).length) {
        setPhysicianFieldErrors(apiFieldErrors);
      }
      toast.error(err.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const sectionProps = (id) => ({
    canEdit,
    editing: editingSection === id,
    saving: saving && editingSection === id,
    onEdit: () => startEdit(id),
    onCancel: cancelEdit,
    onSave: () => saveSection(id),
  });

  const updateContact = (idx, key, value) => {
    setEmergencyContacts((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c)),
    );
  };
  const addContact = () => setEmergencyContacts((prev) => [...prev, emptyEmergencyContact()]);
  const removeContact = (idx) =>
    setEmergencyContacts((prev) => prev.filter((_, i) => i !== idx));

  const updateDiagnosis = (idx, key, value) => {
    setDiagnosisAllergies((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    );
  };
  const selectDiagnosisFromCatalog = (idx, item) => {
    setDiagnosisAllergies((prev) =>
      prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              diagnosisCode: item.code || "",
              primaryDiagnosis: item.label || `${item.code} - ${item.description}`,
              clinicalGroup: item.clinicalGroup || r.clinicalGroup,
              isBillable:
                typeof item.isBillable === "boolean"
                  ? item.isBillable
                  : typeof item.primaryAllowed === "boolean"
                    ? item.primaryAllowed
                    : true,
            }
          : r,
      ),
    );
    if (idx === 0 && item.billingWarning) {
      toast.error(item.billingWarning, { duration: 5000 });
    } else if (item.requiresCodeFirst) {
      toast(item.billingWarning || "Code First required for this diagnosis", {
        icon: "⚠️",
        duration: 4000,
      });
    } else if (item.isBillable === false) {
      toast.error("Not billable as primary diagnosis", { duration: 4000 });
    }
  };
  const clearDiagnosisCatalog = (idx) => {
    setDiagnosisAllergies((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, diagnosisCode: "" } : r)),
    );
  };
  const addDiagnosis = () => {
    setDiagnosisAllergies((prev) => [...prev, emptyDiagnosisAllergy()]);
  };
  const removeDiagnosis = (idx) =>
    setDiagnosisAllergies((prev) => prev.filter((_, i) => i !== idx));

  const updateAllergySelection = (idx, value) => {
    setAllergies((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              allergyName: value,
              otherText:
                value === ALLERGY_OTHER_VALUE ? row.otherText || "" : "",
            }
          : row,
      ),
    );
  };
  const updateAllergyOtherText = (idx, value) => {
    setAllergies((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, otherText: value } : row)),
    );
  };
  const addAllergy = () => {
    setAllergies((prev) => [...prev, emptyAllergy()]);
  };
  const removeAllergy = (idx) =>
    setAllergies((prev) => prev.filter((_, i) => i !== idx));

  const updateReferral = (idx, key, value) => {
    setReferrals((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addReferral = () => setReferrals((prev) => [...prev, emptyReferral()]);
  const removeReferral = (idx) => setReferrals((prev) => prev.filter((_, i) => i !== idx));

  const updateInsurance = (idx, key, value) => {
    setInsurances((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addInsurance = () => setInsurances((prev) => [...prev, emptyInsurance()]);
  const removeInsurance = (idx) => setInsurances((prev) => prev.filter((_, i) => i !== idx));

  const updatePhysician = (idx, key, value) => {
    setPhysicians((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    );
    setPhysicianFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`physicians.${idx}.${key}`];
      return next;
    });
  };
  const selectPhysicianFromCatalog = (idx, item) => {
    setPhysicians((prev) =>
      prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              physicianName: item.displayName || r.physicianName,
              npiNumber: item.npi || "",
              pecosStatus: pecosStatusFromPmd(item.pmd),
              physicianAddress:
                item.fullAddress || item.address || r.physicianAddress,
              physicianPhone: item.phone || r.physicianPhone,
              physicianEmail: item.email || r.physicianEmail,
              physicianFax: item.fax || r.physicianFax,
            }
          : r,
      ),
    );
    setPhysicianFieldErrors((prev) => {
      const next = { ...prev };
      [
        "physicianName",
        "npiNumber",
        "pecosStatus",
        "physicianAddress",
        "physicianPhone",
        "physicianEmail",
        "physicianFax",
      ].forEach((k) => delete next[`physicians.${idx}.${k}`]);
      return next;
    });
  };
  const clearPhysicianCatalog = (idx) => {
    setPhysicians((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, npiNumber: "" } : r)),
    );
  };
  const addPhysician = () => setPhysicians((prev) => [...prev, emptyPhysician()]);
  const removePhysician = (idx) => setPhysicians((prev) => prev.filter((_, i) => i !== idx));

  const viewContacts = buildEmergencyContacts(patient);
  const viewReferrals = buildReferrals(patient);
  const viewInsurances = buildInsurances(patient);
  const viewPhysicians = buildPhysicians(patient);

  return (
    <FormProvider {...form}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {!canEdit ? (
            <p className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg">
              Demographics are read-only for your role
            </p>
          ) : (
            <span className="hidden sm:block" />
          )}
          <button
            type="button"
            onClick={() => {
              const url = demographicsPrintUrl(patient.id);
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ml-auto ${PATIENT_THEME.button.outline}`}
          >
            <PrintIcon size={13} />
            Print Demographics
          </button>
        </div>

        <SectionCard
          title="Personal Information"
          icon={<UserIcon size={14} />}
          tone="personal"
          {...sectionProps("personal")}
        >
          {editingSection === "personal" ? (
            <EditGrid>
              <FormSelect size="sm" label="Status" name="status" register={register} error={errors.status} required placeholder="Select" options={PATIENT_STATUS_SELECT_OPTIONS} />
              <InputDatePicker label={SOC_DATE_LABEL} name="admissionDate" formatSeparator="/" allowManualEntry isRequired labelClassName={dateLabelClass} inputClassName={dateInputClass} />
              {showEocDate ? (
                <InputDatePicker label={EOC_DATE_LABEL} name="eocDate" formatSeparator="/" allowManualEntry isRequired labelClassName={dateLabelClass} inputClassName={dateInputClass} />
              ) : null}
              <FormInput size="sm" label="Last Name" name="lastName" register={register} error={errors.lastName} required />
              <FormInput size="sm" label="First Name" name="firstName" register={register} error={errors.firstName} required />
              <FormInput size="sm" label="Middle Name" name="middleName" register={register} />
              <InputDatePicker label="Date of Birth" name="dateOfBirth" formatSeparator="/" allowManualEntry isRequired labelClassName={dateLabelClass} inputClassName={dateInputClass} />
              <FormSelect size="sm" label="Gender" name="gender" register={register} error={errors.gender} required placeholder="Select" options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]} />
              <FormInput size="sm" label="Language" name="language" register={register} />
              <FormSelect size="sm" label="Marital Status" name="maritalStatus" register={register} placeholder="Select" options={MARITAL_STATUS_OPTIONS} />
              <FormSelect size="sm" label="Race" name="race" register={register} placeholder="Select" options={RACE_OPTIONS} />
              <FormSelect size="sm" label="Religion" name="religion" register={register} placeholder="Select" options={RELIGION_OPTIONS} />
            </EditGrid>
          ) : (
            <FieldGrid>
              <Field label="Status" value={STATUS_LABELS[patient.status] || patient.status} />
              <Field label={SOC_DATE_LABEL} value={displayDateUS(patient.admissionDate)} />
              {statusRequiresEocDate(patient.status) ? (
                <Field label={EOC_DATE_LABEL} value={displayDateUS(patient.eocDate)} />
              ) : null}
              <Field label="Last Name" value={patient.lastName} />
              <Field label="First Name" value={patient.firstName} />
              <Field label="Middle Name" value={patient.middleName} />
              <Field label="Date of Birth" value={displayDateUS(patient.dateOfBirth)} />
              <Field label="Gender" value={patient.gender} />
              <Field label="Language" value={patient.language} />
              <Field label="Marital Status" value={patient.maritalStatus} />
              <Field label="Race" value={patient.race} />
              <Field label="Religion" value={patient.religion} />
            </FieldGrid>
          )}
        </SectionCard>

        <SectionCard
          title="Contact & Location"
          icon={<MapPinIcon size={14} />}
          tone="contact"
          {...sectionProps("contact")}
        >
          {editingSection === "contact" ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3 p-4">
              <FormInput
                size="sm"
                label="Primary Phone"
                name="phone"
                register={register}
                error={errors.phone}
                required
                placeholder="(XXX)XXX-XXXX"
                maxLength={13}
                onInput={(e) => {
                  e.target.value = formatPhone(e.target.value);
                }}
              />
              <FormInput
                size="sm"
                label="Alternate Phone"
                name="alternatePhone"
                register={register}
                error={errors.alternatePhone}
                placeholder="(XXX)XXX-XXXX"
                maxLength={13}
                onInput={(e) => {
                  e.target.value = formatPhone(e.target.value);
                }}
              />
              <FormInput size="sm" label="Email" name="email" type="email" register={register} error={errors.email} />
              <FormInput size="sm" label="Street Address" name="address" register={register} error={errors.address} required />
              <FormInput size="sm" label="Apt / Suite" name="addressLine2" register={register} error={errors.addressLine2} required />
              <FormInput size="sm" label="City" name="city" register={register} />
              <FormInput size="sm" label="State" name="state" register={register} error={errors.state} required />
              <Controller
                name="zip"
                control={control}
                render={({ field }) => (
                  <ZipCodeInput
                    label="Zip Code"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.zip?.message}
                    required
                  />
                )}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-3 p-4">
              <Field label="Primary Phone" value={formatPhone(patient.phone || "") || patient.phone} />
              <Field label="Alternate Phone" value={formatPhone(patient.alternatePhone || "") || patient.alternatePhone} />
              <Field label="Email" value={patient.email} />
              <Field label="Street Address" value={patient.address} />
              <Field label="Apt / Suite" value={patient.addressLine2} />
              <Field label="City" value={patient.city} />
              <Field label="State" value={patient.state} />
              <Field label="Zip" value={patient.zip} />
            </div>
          )}
        </SectionCard>

        <SectionCard
          title={NUTRITION_SECTION_TITLE}
          icon={<AlertCircleIcon size={14} />}
          tone="diagnosis"
          {...sectionProps("diagnosis")}
        >
          {editingSection === "diagnosis" ? (
            <DiagnosisNutritionSubsectionStack>
              <DiagnosisNutritionSubsection title="Diagnosis">
                <MultiRowSection
                  tone="diagnosis"
                  rows={diagnosisAllergies}
                  onAdd={addDiagnosis}
                  onRemove={removeDiagnosis}
                  addLabel="Add Diagnosis"
                  minRows={1}
                  className="space-y-3"
                  renderRow={(row, idx) => (
                    <>
                      <DiagnosisSearchField
                        label={
                          idx === 0
                            ? "Primary Diagnosis"
                            : "Secondary Diagnosis"
                        }
                        value={row.primaryDiagnosis}
                        diagnosisCode={row.diagnosisCode}
                        onChangeText={(text) =>
                          updateDiagnosis(idx, "primaryDiagnosis", text)
                        }
                        onSelectCatalog={(item) =>
                          selectDiagnosisFromCatalog(idx, item)
                        }
                        onClearCatalog={() => clearDiagnosisCatalog(idx)}
                        placeholder="Type code or name (e.g. dia, E11.9)…"
                        className="lg:col-span-6"
                      />
                      <RowSelectField
                        label="Clinical Group"
                        value={row.clinicalGroup}
                        onChange={(e) =>
                          updateDiagnosis(
                            idx,
                            "clinicalGroup",
                            e.target.value,
                          )
                        }
                        options={CLINICAL_GROUP_OPTIONS}
                        placeholder="Select clinical group"
                        className="lg:col-span-3"
                      />
                      <RowBillableField
                        label="Billable"
                        checked={row.isBillable === true}
                        onChange={(checked) =>
                          updateDiagnosis(idx, "isBillable", checked)
                        }
                        className="lg:col-span-2"
                      />
                    </>
                  )}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Allergies">
                <MultiRowSection
                  tone="diagnosis"
                  rows={allergies}
                  onAdd={addAllergy}
                  onRemove={removeAllergy}
                  addLabel="Add Allergy"
                  minRows={0}
                  className="space-y-3"
                  renderRow={(row, idx) => (
                    <AllergyRowFields
                      row={row}
                      index={idx}
                      options={COMMON_ALLERGY_OPTIONS}
                      onSelectChange={(value) =>
                        updateAllergySelection(idx, value)
                      }
                      onOtherTextChange={(value) =>
                        updateAllergyOtherText(idx, value)
                      }
                    />
                  )}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Diet">
                <NutritionDietEditFields
                  nutrition={nutrition}
                  onChange={setNutrition}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Restriction">
                <NutritionRestrictionEditFields
                  nutrition={nutrition}
                  onChange={setNutrition}
                />
              </DiagnosisNutritionSubsection>
            </DiagnosisNutritionSubsectionStack>
          ) : (
            <DiagnosisNutritionViewContent patient={patient} />
          )}
        </SectionCard>

        <SectionCard
          title="Referral Info"
          icon={<HelpingIcon size={14} />}
          tone="referral"
          {...sectionProps("referral")}
        >
          {editingSection === "referral" ? (
            <MultiRowSection
              tone="referral"
              rows={referrals}
              onAdd={addReferral}
              onRemove={removeReferral}
              addLabel="Add Referral"
              renderRow={(row, idx) => (
                <>
                  <RowDateField
                    label="Referral Date"
                    value={row.referralDate}
                    onChange={(v) => updateReferral(idx, "referralDate", v)}
                    className="lg:col-span-3"
                  />
                  <RowSelectField
                    label="Referral Source"
                    value={row.referralSource}
                    onChange={(e) => {
                      const next = e.target.value;
                      updateReferral(idx, "referralSource", next);
                      const match = findReferral(next);
                      if (match) {
                        // Keep source = selected library name; name mirrors catalog
                        updateReferral(idx, "referralName", match.referralName);
                      }
                    }}
                    options={ensureOption(
                      libraryReferralOptions.length
                        ? libraryReferralOptions
                        : REFERRAL_SOURCE_OPTIONS,
                      row.referralSource,
                    )}
                    className="lg:col-span-4"
                  />
                  <RowTextField
                    label="Referral Name"
                    value={row.referralName}
                    onChange={(e) => updateReferral(idx, "referralName", e.target.value)}
                    className="lg:col-span-4"
                  />
                </>
              )}
            />
          ) : (
            <ReadOnlyRows
              rows={viewReferrals}
              emptyText="No referral info added"
              columns={[
                { key: "referralDate", label: "Referral Date" },
                { key: "referralSource", label: "Referral Source" },
                { key: "referralName", label: "Referral Name" },
              ]}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Insurance Info"
          icon={<ShieldIcon size={14} />}
          tone="insurance"
          {...sectionProps("insuranceInfo")}
        >
          {editingSection === "insuranceInfo" ? (
            <MultiRowSection
              tone="insurance"
              rows={insurances}
              onAdd={addInsurance}
              onRemove={removeInsurance}
              addLabel="Add Insurance"
              minRows={1}
              renderRow={(row, idx) => (
                <>
                  <RowDateField
                    label="Start Date"
                    value={row.startDate}
                    onChange={(v) => updateInsurance(idx, "startDate", v)}
                    className="lg:col-span-2"
                  />
                  <InsuranceNameField
                    key={`insurance-name-${idx}`}
                    label="Insurance Name"
                    value={row.insuranceName}
                    onChange={(v) => updateInsurance(idx, "insuranceName", v)}
                    required
                    className="lg:col-span-3"
                  />
                  <RowTextField
                    label="Policy #"
                    value={row.policyNumber}
                    onChange={(e) => updateInsurance(idx, "policyNumber", e.target.value)}
                    required
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Group"
                    value={row.insuranceGroup}
                    onChange={(e) => updateInsurance(idx, "insuranceGroup", e.target.value)}
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Co-Pay"
                    value={row.patientCoPay}
                    onChange={(e) => updateInsurance(idx, "patientCoPay", e.target.value)}
                    className="lg:col-span-2"
                  />
                </>
              )}
            />
          ) : (
            <ReadOnlyRows
              rows={viewInsurances}
              emptyText="No insurance info added"
              columns={[
                { key: "startDate", label: "Start Date" },
                { key: "insuranceName", label: "Insurance Name" },
                { key: "policyNumber", label: "Policy #" },
                { key: "insuranceGroup", label: "Group" },
                { key: "patientCoPay", label: "Co-Pay" },
              ]}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Physician Info"
          icon={<DoctorIcon size={14} />}
          tone="physician"
          {...sectionProps("physicianInfo")}
        >
          {editingSection === "physicianInfo" ? (
            <MultiRowSection
              tone="physician"
              rows={physicians}
              onAdd={addPhysician}
              onRemove={removePhysician}
              addLabel="Add Physician"
              renderRow={(row, idx) => (
                <>
                  <RowSelectField
                    label="Type"
                    value={row.physicianType}
                    onChange={(e) => updatePhysician(idx, "physicianType", e.target.value)}
                    options={PHYSICIAN_TYPE_OPTIONS}
                    className="lg:col-span-2"
                    error={physicianFieldErrors[`physicians.${idx}.physicianType`]}
                  />
                  <PhysicianSearchField
                    label="Physician Name"
                    value={row.physicianName}
                    npiNumber={row.npiNumber}
                    onChangeText={(text) =>
                      updatePhysician(idx, "physicianName", text)
                    }
                    onSelectCatalog={(item) =>
                      selectPhysicianFromCatalog(idx, item)
                    }
                    onClearCatalog={() => clearPhysicianCatalog(idx)}
                    placeholder="Search by name or NPI number…"
                    className="lg:col-span-3"
                    required
                    error={physicianFieldErrors[`physicians.${idx}.physicianName`]}
                  />
                  <RowTextField
                    label="Address"
                    value={row.physicianAddress}
                    onChange={(e) => updatePhysician(idx, "physicianAddress", e.target.value)}
                    className="lg:col-span-4"
                    error={physicianFieldErrors[`physicians.${idx}.physicianAddress`]}
                  />
                  <PhysicianSearchField
                    label="NPI Number"
                    mode="npi"
                    value={row.npiNumber}
                    onChangeText={(text) =>
                      updatePhysician(idx, "npiNumber", text)
                    }
                    onSelectCatalog={(item) =>
                      selectPhysicianFromCatalog(idx, item)
                    }
                    placeholder="Search by NPI number…"
                    className="lg:col-span-2"
                    error={physicianFieldErrors[`physicians.${idx}.npiNumber`]}
                  />
                  <RowSelectField
                    label="PECOS Status"
                    value={row.pecosStatus}
                    onChange={(e) =>
                      updatePhysician(idx, "pecosStatus", e.target.value)
                    }
                    options={PECOS_STATUS_OPTIONS}
                    placeholder="Select"
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.pecosStatus`]}
                  />
                  <RowTextField
                    label="Phone"
                    value={row.physicianPhone}
                    onChange={(e) => updatePhysician(idx, "physicianPhone", formatPhone(e.target.value))}
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.physicianPhone`]}
                  />
                  <RowTextField
                    label="Fax"
                    value={row.physicianFax}
                    onChange={(e) => updatePhysician(idx, "physicianFax", formatPhone(e.target.value))}
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.physicianFax`]}
                  />
                </>
              )}
            />
          ) : (
            <ReadOnlyRows
              rows={viewPhysicians}
              emptyText="No physician info added"
              columns={[
                { key: "physicianType", label: "Type" },
                { key: "physicianName", label: "Name" },
                { key: "physicianAddress", label: "Address" },
                { key: "npiNumber", label: "NPI" },
                {
                  key: "pecosStatus",
                  label: "PECOS Status",
                  format: (v) =>
                    v === "enrolled"
                      ? "Enrolled"
                      : v === "un-enrolled"
                        ? "Un-enrolled"
                        : v || "—",
                },
                {
                  key: "physicianPhone",
                  label: "Phone",
                  format: (v) => formatPhone(v || "") || v,
                },
                {
                  key: "physicianFax",
                  label: "Fax",
                  format: (v) => formatPhone(v || "") || v,
                },
              ]}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Primary Pharmacy"
          icon={<PillIcon size={14} />}
          tone="pharmacy"
          {...sectionProps("pharmacy")}
        >
          {editingSection === "pharmacy" ? (
            <div className="p-4">
              <PrimaryFacilityEditFields
                variant="pharmacy"
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                typeOptions={PHARMACY_TYPE_OPTIONS}
              />
            </div>
          ) : (
            <PrimaryFacilityViewFields
              variant="pharmacy"
              patient={patient}
              Field={Field}
              formatPhoneFn={formatPhone}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Primary Hospital"
          icon={<BuildingIcon size={14} />}
          tone="hospital"
          {...sectionProps("hospital")}
        >
          {editingSection === "hospital" ? (
            <div className="p-4">
              <PrimaryFacilityEditFields
                variant="hospital"
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                typeOptions={HOSPITAL_TYPE_OPTIONS}
              />
            </div>
          ) : (
            <PrimaryFacilityViewFields
              variant="hospital"
              patient={patient}
              Field={Field}
              formatPhoneFn={formatPhone}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Emergency Contacts"
          icon={<PhoneIcon size={14} />}
          tone="emergency"
          {...sectionProps("emergency")}
        >
          {editingSection === "emergency" ? (
            <MultiRowSection
              tone="emergency"
              rows={emergencyContacts}
              onAdd={addContact}
              onRemove={removeContact}
              addLabel="Add contact"
              renderRow={(contact, idx) => (
                <>
                  <RowTextField
                    label="Last Name"
                    value={contact.lastName}
                    onChange={(e) => updateContact(idx, "lastName", e.target.value)}
                    className="lg:col-span-3"
                  />
                  <RowTextField
                    label="First Name"
                    value={contact.firstName}
                    onChange={(e) => updateContact(idx, "firstName", e.target.value)}
                    className="lg:col-span-3"
                  />
                  <RowSelectField
                    label="Relation"
                    value={contact.relationship}
                    onChange={(e) => updateContact(idx, "relationship", e.target.value)}
                    options={buildRelationshipOptions(contact.relationship)}
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Phone"
                    value={contact.phone}
                    onChange={(e) =>
                      updateContact(idx, "phone", formatPhone(e.target.value))
                    }
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                  />
                </>
              )}
            />
          ) : viewContacts.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-500">
              No emergency contacts added
            </p>
          ) : (
            <div className="divide-y divide-amber-100">
              {viewContacts.map((contact, idx) => {
                const fullName =
                  [contact.lastName, contact.firstName]
                    .filter(Boolean)
                    .join(", ") || "—";
                return (
                  <div
                    key={idx}
                    className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 px-4 py-3"
                  >
                    <Field label="Name" value={fullName} />
                    <Field
                      label="Relation"
                      value={contact.relationship || "—"}
                    />
                    <Field
                      label="Phone"
                      value={
                        formatPhone(contact.phone || "") ||
                        contact.phone ||
                        "—"
                      }
                      className="col-span-2 md:col-span-1"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        <PatientNotesSection
          patientId={patient.id}
          canEditNotes={canEditNotes}
          onSaved={onSaved}
        />
      </div>
    </FormProvider>
  );
};

export default PatientDemographicsTab;
